import React from 'react';

import { Form, Switch } from 'antd-v5';
import cn from 'classnames';
import { DateTime } from 'luxon';
import { FaFileCsv } from 'react-icons/fa6';
import { MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from 'scouting-ui/lib';
import { TextArea } from 'scouting-ui/lib/Input';
import { cssVars } from 'scouting-ui/lib/variables';

import type { ImportMBFileUploadOptions } from '@appTypes/esb';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { intl } from '@shared/localization';
import { formatToUIDate } from '@utils/datesLuxon';

import {
  ACCEPT_CAMP_TYPES,
  CAMP_IMPORT_ITEMS,
  CAMP_MAX_FILE_SIZE,
  CAMP_NOTE_MAX_LENGTH,
} from '../../constants';
import type { CampImportItem } from '../../types';
import ImportDropzone from '../ImportDropzone';
import styles from './CampImportSetup.module.scss';

const FORM_NAME = 'campImportSetup';
const NOTE_HINT_ID = `${FORM_NAME}_commentHint`;

export interface CampImportSetupProps {
  /** The staged file; without one only the dropzone shows */
  fileName?: string;
  initialValues?: Partial<ImportMBFileUploadOptions>;
  onSelectFile: (file: File) => void;
  /** Drops the staged file (Remove or Cancel) */
  onDiscard: () => void;
  onImport: (values: ImportMBFileUploadOptions) => void;
}

const buildDefaults = (): ImportMBFileUploadOptions => ({
  comment: intl.formatMessage(
    { id: 'advancementImport.camp.setup.defaultNote' },
    { date: formatToUIDate(DateTime.now()) },
  ),
  meritBadgeRequirements: true,
  completeMeritBadges: true,
  leaderApproveMeritBadges: true,
});

const CampImportSetup: React.FC<CampImportSetupProps> = props => {
  const { fileName, initialValues, onSelectFile, onDiscard, onImport } = props;

  const [form] = Form.useForm<ImportMBFileUploadOptions>();
  // `preserve` reads the store, so the selection survives the fields unmounting
  const selected: CampImportItem[] =
    Form.useWatch(
      values =>
        CAMP_IMPORT_ITEMS.filter(item => values[item.name]).map(
          item => item.name,
        ),
      { form, preserve: true },
    ) ?? [];

  const isStaged = !!fileName;
  const noneSelected = selected.length === 0;
  const selectedLabels = CAMP_IMPORT_ITEMS.filter(item =>
    selected.includes(item.name),
  ).map(item => intl.formatMessage({ id: item.labelId }));

  // Resetting first means the next staged file starts from the defaults
  const handleDiscard = () => {
    form.resetFields();
    onDiscard();
  };

  return (
    // Mounted in both states so `resetFields` can run before the fields unmount
    <Form
      form={form}
      name={FORM_NAME}
      layout="vertical"
      requiredMark={false}
      initialValues={{ ...buildDefaults(), ...initialValues }}
      onFinish={onImport}
      className={styles.form}
    >
      {isStaged ? (
        <div className={cn(styles.card, styles.expanded)}>
          <div className={styles.file}>
            <FaFileCsv className={styles.fileIcon} />
            <Text strong color="color-primary" className={styles.fileName}>
              {fileName}
            </Text>
            <Button type="default" danger onClick={handleDiscard}>
              <FormattedMessage id="shared.remove" />
            </Button>
          </div>

          <div className={styles.heading}>
            <Text strong className={styles.title}>
              <FormattedMessage id="advancementImport.camp.setup.title" />
            </Text>
            <Text font="body-caption" type="secondary">
              <FormattedMessage id="advancementImport.camp.setup.subtitle" />
            </Text>
          </div>

          <ul className={styles.options}>
            {CAMP_IMPORT_ITEMS.map(item => {
              const label = intl.formatMessage({ id: item.labelId });
              const isOn = selected.includes(item.name);
              return (
                <li key={item.name} className={styles.option}>
                  <div className={styles.optionText}>
                    <Text strong>{label}</Text>
                    <Text
                      font="body-caption"
                      strong
                      color={isOn ? 'color-success' : 'color-text-muted'}
                      className={styles.status}
                    >
                      <FormattedMessage
                        id={
                          isOn
                            ? 'advancementImport.camp.setup.willImport'
                            : 'advancementImport.camp.setup.willNotImport'
                        }
                      />
                    </Text>
                  </div>
                  <Form.Item name={item.name} valuePropName="checked" noStyle>
                    <Switch aria-label={label} />
                  </Form.Item>
                </li>
              );
            })}
          </ul>

          <Form.Item
            label={
              <FormattedMessage id="advancementImport.camp.setup.noteLabel" />
            }
            htmlFor={`${FORM_NAME}_comment`}
            className={styles.note}
          >
            <div id={NOTE_HINT_ID} className={styles.noteHint}>
              <Text font="body-caption" type="secondary">
                {noneSelected ? (
                  <FormattedMessage id="advancementImport.camp.setup.noteNone" />
                ) : (
                  <FormattedMessage
                    id="advancementImport.camp.setup.noteAttachedTo"
                    values={{
                      items: (
                        <Text font="body-caption" strong color="color-primary">
                          {selectedLabels.join(', ')}
                        </Text>
                      ),
                    }}
                  />
                )}
              </Text>
            </div>
            <Form.Item name="comment" noStyle>
              <TextArea
                showCount
                maxLength={CAMP_NOTE_MAX_LENGTH}
                rows={2}
                disabled={noneSelected}
                aria-describedby={NOTE_HINT_ID}
              />
            </Form.Item>
          </Form.Item>

          <div className={styles.notice}>
            <MdInfo
              className={styles.noticeIcon}
              color={cssVars['color-info']}
            />
            <Text font="body-caption">
              <FormattedMessage id="advancementImport.camp.setup.processingTime" />
            </Text>
          </div>
        </div>
      ) : (
        <div className={styles.card}>
          <ImportDropzone
            accept={ACCEPT_CAMP_TYPES}
            maxSize={
              featureFlags.getFlag('SBL_7000_SKIP_CAMP_FILE_SIZE_LIMIT')
                ? undefined
                : CAMP_MAX_FILE_SIZE
            }
            dropLabel={
              <FormattedMessage id="advancementImport.camp.dragHere" />
            }
            onSelectFile={onSelectFile}
          />
        </div>
      )}

      {isStaged && (
        <div className={styles.actions}>
          <Button type="default" onClick={handleDiscard}>
            <FormattedMessage id="shared.cancel" />
          </Button>
          <Button htmlType="submit" disabled={noneSelected}>
            <FormattedMessage id="advancementImport.camp.setup.import" />
          </Button>
        </div>
      )}
    </Form>
  );
};

export default CampImportSetup;
